import "./FormationGrid.css";

import { useMemo } from "react";
import GridLayout from "react-grid-layout";
import { useTranslation } from "react-i18next";

import {
  IFormationLayout,
  ISoccerMatchEventsWithCount,
  ISoccerMatchMissedPenaltyEvent,
  ISoccerMatchPenaltyShotEvent,
  ISoccerPenaltyEvent,
  SoccerMatchEventType,
} from "../../../../types";
import captainLogo from "../../assets/icons/captain.svg";
import ShirtIcon from "../../assets/icons/Shirt";
import {
  IGroupedData,
  IGroupedPlayerData,
  IGroupedPlayers,
  ISoccerMatchEventWithPlayerAction,
  useContainerDimensions,
} from "../../hooks";
import { IPlayers } from "../../utils";
import { NoDataAvailable } from "../NoDataAvailable";
import GridMatchEvents from "./GridMatchEvents/GridMatchEvents";

interface FormationGridProps {
  layout: IFormationLayout[];
  lineups: IPlayers[];
  shirtColor: string;
  textColor: string;
  currentGroupedEventData: IGroupedData;
  formationUsed: string;
}

// Constants
const DEFAULT_ROW_HEIGHT = 80;
const DEFAULT_GRID_WIDTH = 468;
const DEFAULT_GRID_HEIGHT = 600;
const DEFAULT_ROW_COUNT = 9;
const GRID_MARGIN: [number, number] = [-30, 5];

export default function FormationGrid({
  layout,
  lineups,
  shirtColor,
  textColor,
  currentGroupedEventData,
  formationUsed,
}: FormationGridProps) {
  const { t } = useTranslation();

  const playerEventsData = currentGroupedEventData?.players || [];

  /**
   * Groups events by type and processes them according to business rules
   */
  const groupEventsByType = (
    events: ISoccerMatchEventsWithCount[]
  ): { [eventType: string]: ISoccerMatchEventsWithCount[] } => {
    return events.reduce((groups, event) => {
      const eventType = event.eventType as string;
      if (!groups[eventType]) {
        groups[eventType] = [];
      }
      groups[eventType].push(event);
      return groups;
    }, {} as { [eventType: string]: ISoccerMatchEventsWithCount[] });
  };

  /**
   * Processes countable events (goals, penalties) by adding count
   */
  const processCountableEvents = (
    events: ISoccerMatchEventsWithCount[]
  ): ISoccerMatchEventsWithCount => {
    const firstEvent = { ...events[0] };
    firstEvent.count = events.length;
    return firstEvent;
  };

  /**
   * Gets the most recent card event based on timestamp
   */
  const getMostRecentCard = (
    events: ISoccerMatchEventsWithCount[]
  ): ISoccerMatchEventsWithCount => {
    return events.reduce((latest, current) => {
      return new Date(current.timestamp) > new Date(latest.timestamp)
        ? current
        : latest;
    });
  };

  /**
   * Processes events for a single player according to business rules
   */
  const processPlayerEvents = (
    playerData: IGroupedPlayerData
  ): IGroupedPlayerData => {
    const processedEvents: ISoccerMatchEventsWithCount[] = [];
    const eventGroups = groupEventsByType(
      playerData.events as ISoccerMatchEventsWithCount[]
    );

    for (const [eventType, events] of Object.entries(eventGroups)) {
      if (events.length === 0) continue;

      switch (eventType as SoccerMatchEventType) {
        case SoccerMatchEventType.GOAL:
        case SoccerMatchEventType.MISSED_PENALTY:
        case SoccerMatchEventType.PENALTY_SHOT:
          processedEvents.push(processCountableEvents(events));
          break;

        case SoccerMatchEventType.CARD:
          processedEvents.push({ ...getMostRecentCard(events) });
          break;

        default:
          // For other event types (substitutions, etc.), keep all events
          events.forEach((event) => {
            processedEvents.push({ ...event });
          });
          break;
      }
    }

    return {
      ...playerData,
      events: processedEvents,
    };
  };

  /**
   * Restructures player events data according to business rules
   */
  const restructurePlayerEvents = (
    playersData: IGroupedPlayers
  ): IGroupedPlayers | undefined => {
    if (!playersData) return undefined;

    const restructuredData: IGroupedPlayers = {};

    for (const [playerId, playerData] of Object.entries(playersData)) {
      restructuredData[playerId] = processPlayerEvents({
        ...playerData,
      });
    }

    return restructuredData;
  };

  /**
   * Groups penalty-related events together
   */
  const groupPenaltyEvents = (
    events: ISoccerMatchEventWithPlayerAction[]
  ): ISoccerMatchEventWithPlayerAction[] => {
    return events.reduce((acc: ISoccerMatchEventWithPlayerAction[], curr) => {
      const isPenaltyEvent =
        curr.eventType === SoccerMatchEventType.MISSED_PENALTY ||
        curr.eventType === SoccerMatchEventType.PENALTY_SHOT;

      if (isPenaltyEvent) {
        let penaltyGroup = acc.find(
          (e) => e.eventType === SoccerMatchEventType.PENALTY
        ) as ISoccerPenaltyEvent;

        if (!penaltyGroup) {
          // Create new penalty group
          penaltyGroup = {
            ...curr,
            eventType: SoccerMatchEventType.PENALTY,
            count: 1,
            subEvents: [{ ...curr }] as (
              | ISoccerMatchPenaltyShotEvent
              | ISoccerMatchMissedPenaltyEvent
            )[],
          };
          acc.push(penaltyGroup as ISoccerMatchEventWithPlayerAction);
        } else {
          // Add to existing penalty group
          penaltyGroup.count += 1;
          penaltyGroup.subEvents.push({
            ...curr,
          } as ISoccerMatchPenaltyShotEvent | ISoccerMatchMissedPenaltyEvent);
        }
      } else {
        // Add non-penalty events as-is
        acc.push(curr);
      }

      return acc;
    }, []);
  };

  /**
   * Formats player name for display (abbreviates long names)
   */
  const formatPlayerName = (name: string): string => {
    const nameParts = name?.split(" ");
    if (nameParts?.length > 1) {
      const firstName = nameParts[0].charAt(0);
      const lastName = nameParts[nameParts.length - 1];
      return `${firstName}. ${lastName}`;
    }
    return name;
  };

  /**
   * Calculates optimal row height based on container dimensions
   */
  const calculateRowHeight = (gridHeight: number): number => {
    if (!gridHeight || gridHeight <= 0) {
      return DEFAULT_ROW_HEIGHT;
    }

    const buffer = formationUsed.length > 5 ? 1.08 : 1.2;
    return (gridHeight * buffer) / DEFAULT_ROW_COUNT;
  };

  /**
   * Determines grid columns based on width
   */

  const getGridColumns = (gridWidth: number): number => {
    return gridWidth <= 350 ? 11 : gridWidth > 500 ? 10.4 : 10.75;
  };

  // Memoized calculations
  const restructuredEventsData = useMemo(
    () => restructurePlayerEvents(playerEventsData),
    [playerEventsData]
  );

  const { width: gridWidth, height: gridHeight } = useContainerDimensions(
    ".formation-container",
    DEFAULT_GRID_WIDTH,
    DEFAULT_GRID_HEIGHT,
    768 // max width = 768px
  );

  const rowHeight = calculateRowHeight(gridHeight);
  const gridColumns = getGridColumns(gridWidth);

  // Early return for no lineups
  if (!lineups.length) {
    return (
      <div className="formation-container">
        <div className="line-ups-not-available">
          <NoDataAvailable
            title={t("errorState.noDataAvailable.title")}
            description={t("errorState.noDataAvailable.lineUpDescription")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="formation-container">
      <div className="formation-grid">
        <GridLayout
          className="layout"
          layout={layout}
          cols={gridColumns}
          rowHeight={rowHeight}
          width={gridWidth}
          isDraggable={false}
          isResizable={false}
          compactType={null}
          preventCollision={true}
          margin={GRID_MARGIN}
        >
          {layout.map((pos, index) => {
            const player = lineups[index < lineups.length ? index : 0];
            const playerEvents = (restructuredEventsData?.[player.id] ||
              {}) as IGroupedPlayerData;
            const playerName = formatPlayerName(player.name);
            const groupedEvents = groupPenaltyEvents(playerEvents.events || []);

            return (
              <div data-grid={pos} key={pos.i} className="player-tile-wrapper">
                <div className="player-tile">
                  <ShirtIcon color={shirtColor} />
                  <span
                    className="player-number"
                    style={{ color: textColor || "var(--white)" }}
                  >
                    {player?.jerseyNumber || "?"}
                  </span>
                  {groupedEvents?.map((event, idx) => (
                    <GridMatchEvents
                      key={`${event.timestamp}-${idx}`}
                      event={event as ISoccerMatchEventsWithCount}
                    />
                  ))}
                </div>
                <div className="player-name">
                  <span>{player ? playerName : pos.i.split("-")[0]}</span>
                  {player.isCaptain && (
                    <img
                      src={captainLogo}
                      className="player-captain-logo"
                      alt="captain logo"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </GridLayout>
      </div>
    </div>
  );
}
